/**
 * Course detail — the page a learner reads before deciding to enrol.
 *
 * Built to the prototype's `#screen-course`, section for section:
 *
 *     breadcrumb → hero → About this course → Before you start → What you'll learn
 *     → How you're graded → Course outline → Meet your instructors
 *
 * WHAT IS NOT HERE, AND WHY. The prototype also carries Reviews, FAQs and "Students also
 * bought". Open edX has no course reviews, no per-course FAQ store and no recommendation
 * engine, so there is no data behind any of the three. They are omitted rather than
 * mocked: a star rating nobody gave, on a page whose only job is to help someone decide,
 * is the one kind of filler that actually costs the reader something.
 *
 * THE SECTIONS THAT ARE HERE ALL DISAPPEAR WHEN THEY ARE EMPTY. That is the rule this
 * page is built around, and it is why it replaced what came before. The old page rendered
 * `overview` — course HTML from Studio — which on an unedited course is the platform's
 * DEFAULT TEMPLATE: headings, placeholder staff photographs, and paragraphs instructing
 * the author to write a description. It looked like a finished page about a course that
 * did not exist. Everything below either has real author content behind it or is not
 * rendered at all, so an unfilled course reads as sparse, which is honest, instead of
 * reading as complete, which was a lie.
 *
 * Three calls, because they answer three different questions and two of them are public:
 *
 *   /api/courses/v1/courses/{id}            what the course IS            (public)
 *   /api/tenant/v1/courses/{id}/about       what the AUTHOR wrote about it (public, ours)
 *   /api/course_home/course_metadata/{id}   where I STAND in it           (public)
 *
 * The third is what makes the primary action honest without a session round-trip: it
 * returns `username: null` for a visitor, `is_enrolled` for a learner, and `is_staff` for
 * the course team — who can open the courseware without an enrolment and must not be told
 * to enrol first.
 *
 * The second is ours because the fields the page is made of exist in the platform and
 * reach no learner API. See the note on `getCourseAbout` in catalogue.ts. It is allowed
 * to fail: everything derived from it is conditional, so a 404 or an outage degrades this
 * to the hero and the enrolment card rather than to an error page.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Icon, Spinner } from '../../shared/ui';
import { text } from '../../shared/lib/config';
import { ApiError } from '../../shared/lib/openedx';
import { loadSession } from '../../shared/lib/session';
import {
  listLearningPaths,
  pathContainsCourse,
  pathHref,
  PATH_SEAM_KEY,
  pct,
  type LearningPathView,
} from '../../shared/lib/paths';
import {
  authoredOverview,
  courseHref,
  courseHeroImage,
  courseImage,
  enrol,
  explainEnrolmentFailure,
  formatDate,
  getCourse,
  getCourseAbout,
  getCourseMetadata,
  learnHref,
  NotOurCourse,
  startLabel,
  type AboutGrading,
  type AboutInstructor,
  type AboutOutline,
  type AboutSubsection,
  type CatalogueCourse,
  type CourseAbout,
  type CourseDetail,
  type CourseMetadata,
  type UnitKind,
} from '../../shared/lib/catalogue';
import {
  beginCheckout,
  buyState,
  coursePriceView,
  explainPricingFailure,
  formatMoney,
  pathwayPriceView,
  PRICE_COPY,
  priceLabel,
  type BuyState,
  type PriceView,
  type PricingSeam,
} from '../../shared/lib/pricing';
import { useCoursePrice, usePathwayPrices } from '../../shared/lib/usePricing';
import { PriceBlock, ProcessingNote, UnavailableNotice } from '../../shared/ui/price';
import { CourseCertificateSection } from './CourseCertificate';
import { gradesHref } from '../../shared/lib/grades';
import VerifyAccountNotice, { useAccountVerified } from '../VerifyAccountNotice';
import './learner.css';
// The pathway chips and the sidebar panel. Kept in the paths stylesheet rather than
// copied into learner.css so the two path screens and this one cannot drift apart.
import './paths.css';

export default function CourseHome() {
  const { courseId = '' } = useParams();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  const course = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => getCourse(courseId),
    retry: false,
  });

  // Deliberately NOT gated on `course` succeeding, and deliberately never fatal. This is
  // the page's content; the course record is its identity. Either can arrive first and
  // the page is renderable with only the second.
  const about = useQuery({
    queryKey: ['course-about', courseId],
    queryFn: () => getCourseAbout(courseId),
    retry: false,
  });

  const metadata = useQuery({
    queryKey: ['course-metadata', courseId],
    queryFn: () => getCourseMetadata(courseId),
    retry: false,
  });

  /**
   * PATHWAY CONTEXT. Additive, and never fatal.
   *
   * A course that belongs to no learning path — which is every course on an academy with
   * no paths, and will be most courses on one that has them — renders this page EXACTLY as
   * it did before: every consumer below checks for an empty list first and returns null.
   * The query failing has the same effect as it returning nothing, which is why it is not
   * gated on anything and its error is not surfaced HERE. Pathways are context; the course
   * is the page.
   *
   * THE FAILURE IS NO LONGER QUARANTINED TO THIS PAGE, though, and that is the price of
   * sharing the key rather than paying for a private copy — said here rather than left for
   * somebody to find. This query now writes into `PATH_SEAM_KEY`, so a failure from a course
   * page leaves that entry in `status: 'error'` and `/paths` and `/paths/{key}` will show
   * their own error state on arrival, before refetching on mount and recovering. That was
   * already true between those three surfaces, which have always shared this key and this
   * `retry: false`; this page joins the set rather than creating the property.
   */
  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const signedIn = session.data?.authenticated === true;

  /*
   * THE SAME QUERY KEY AS /paths, THE DASHBOARD AND THE PATHWAY DETAIL PAGE, on purpose —
   * and it is the fix for a duplicate this page was paying on every visit.
   *
   * It used to be its own key, `['course-paths', courseId, signedIn]`, calling
   * `listPathsContainingCourse`. That function funnelled into the SAME `fetchAllPaths()` and
   * the SAME one URL as `listLearningPaths`, so the body was byte-identical and React Query
   * could not tell: two cache names over one response. Browsing five course pages downloaded
   * the whole academy's pathway catalogue five times to render two "part of this path" chips.
   *
   * MEASURED on the shared dev deployment, 2026-08-18: 20 paths over 45 distinct step
   * courses, ~68.7 KB per copy. Browsing five course pages in one session cost five
   * `learner/paths` requests and 343,522 bytes before this key, and one request and 68,708
   * bytes after it. The chips rendered are identical either way — the same path links, on
   * all five pages.
   *
   * NOT "one request per session", and the difference is worth stating: the client's default
   * `staleTime` is 30s, so a browse that dawdles past it refetches the list ONCE more on the
   * next mount. A 46s browse of the same five pages cost two. The change is from one fetch
   * per course page to at most one per stale window, which is the same shape the other three
   * surfaces have always had.
   *
   * The filtering moved into `select`, which runs over the cached list and makes no request
   * of its own. `PATH_SEAM_KEY` is exported by `paths.ts` precisely so a fourth surface
   * cannot spell the key differently and silently pay again — that note is what this change
   * was asking for.
   */
  const paths = useQuery({
    queryKey: PATH_SEAM_KEY(signedIn),
    queryFn: () => listLearningPaths(signedIn),
    enabled: !session.isPending,
    retry: false,
    select: (views) => views.filter((view) => pathContainsCourse(view, courseId)),
  });
  const inPaths = paths.data ?? [];

  /**
   * WHAT THIS COURSE COSTS. One request, for THIS COURSE, whatever else is on the site.
   *
   * A batch of one, deliberately: this page must render its own price when somebody lands on
   * its URL cold, without a catalogue payload in cache. That is the property the pathway seam
   * gave up when the detail page started reading the list's cache key — cheap at seventeen
   * pathways, and at a thousand it means one course page downloading the academy.
   *
   * Not gated on the session. A price is the same integer for everybody, so waiting for
   * `session` would delay it for no gain — and putting `signedIn` in the key would fetch it
   * twice, once before the session resolves and once after.
   */
  const priceSeam = useCoursePrice(courseId);
  const price = coursePriceView(priceSeam, courseId);

  /**
   * The prices of the pathways this course is IN — for the one cross-sell line in the spec
   * (R6.4): "Also available in {pathway} for {price} — save {saving}."
   *
   * Bounded by the paths this course belongs to, which is one or two, not the academy's
   * pathway catalogue. Keyed on their keys, so it is one small request and it is shared with
   * anything else asking about the same paths.
   */
  const pathKeys = useMemo(() => inPaths.map((view) => view.path.key), [inPaths]);
  const pathPrices = usePathwayPrices(pathKeys);

  const enrolment = useMutation({
    mutationFn: () => enrol(courseId),
    onSuccess: () => {
      // The button's whole state comes from `is_enrolled`, so refetch rather than
      // patching a local flag — an optimistic flip that the server never agreed to sends
      // people into a course they are not in.
      void queryClient.invalidateQueries({ queryKey: ['course-metadata', courseId] });
      void queryClient.invalidateQueries({ queryKey: ['learner-home'] });
    },
  });

  /**
   * BUY. Hands the learner to Stripe's own page and nothing more.
   *
   * The browser sends the course key and a return path — never an amount. What is charged is
   * the Stripe Price sibling F synced, which is why a number from a browser is neither sent
   * nor trusted (spec R2.5, foundation §4.6). No promotion code is collected either: the
   * learner types one into Stripe's own field (D4, coupons FR-5.1).
   *
   * `window.location.assign`, not a router navigation: the destination is Stripe's origin.
   * A failure is reported in the card with the server's own sentence and nothing is charged.
   */
  const checkout = useMutation({
    mutationFn: () =>
      beginCheckout({
        kind: 'course',
        key: courseId,
        returnPath: `/courses/${encodeURIComponent(courseId)}`,
      }),
    onSuccess: (session) => window.location.assign(session.checkout_url),
  });

  /**
   * Finish an enrolment — or a purchase — the visitor started before signing in.
   *
   * Without this the sign-in round trip loses the intent: you press Enrol, sign in, land
   * back here and have to press it again. The flag is dropped from the url immediately so
   * a reload does not re-enrol.
   *
   * `?buy=1` is the paid twin of `?enrol=1` (spec R6.4, R6.10) and is handled by the same
   * effect on purpose: one place decides what a returning visitor's intent was. It resumes
   * only for a price this build believes is buyable — a learner who signed in during a
   * pricing outage must land on the stated reason, not on a checkout for a price nobody
   * could read.
   */
  const wantsEnrolment = params.get('enrol') === '1';
  const wantsPurchase = params.get('buy') === '1';
  useEffect(() => {
    if (!metadata.data || (!wantsEnrolment && !wantsPurchase)) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (!metadata.data.username || metadata.data.is_enrolled) return;
    if (wantsPurchase) {
      if (price.mode === 'paid' && price.buyable) checkout.mutate();
      return;
    }
    enrolment.mutate();
  }, [wantsEnrolment, wantsPurchase, metadata.data, price.mode, price.buyable]);

  if (course.isPending) {
    return (
      <main className="course">
        <Spinner label="Loading this course…" />
      </main>
    );
  }

  if (course.isError) return <CourseUnavailable error={course.error} />;

  const detail = about.data;

  return (
    <main className="course">
      <Breadcrumb title={detail?.title || course.data.name} />

      <CourseHero course={course.data} about={detail} inPaths={inPaths} />

      <div className="course__grid">
        <div>
          <AboutSection course={course.data} about={detail} />
          <Prerequisites about={detail} />
          <WhatYouWillLearn about={detail} />
          <HowYouAreGraded grading={detail?.grading ?? null} />
          <CourseOutline outline={detail?.outline} />
          {/*
            WHAT FINISHING THIS COURSE IS WORTH, AND WHETHER THIS LEARNER HAS IT.
            After the outline, before the instructors: the outline is what the course
            contains and this is what comes out of it, which is the order somebody reads the
            page in. Renders NOTHING for a visitor who is not enrolled, and nothing for a
            course that awards no certificate — see the component.

            THIS IS THE COURSE CERTIFICATE ONLY. The learning path certificate is a
            different record on a different page; neither state is ever derived from the
            other.
          */}
          <CourseCertificateSection
            courseId={courseId}
            enrolled={metadata.data?.is_enrolled === true}
          />
          <Instructors instructors={detail?.instructors ?? []} />
        </div>

        <aside className="course__side">
          <ActionCard
            course={course.data}
            about={detail}
            inPaths={inPaths}
            pathPrices={pathPrices}
            price={price}
            metadata={metadata.data}
            metadataFailed={metadata.isError}
            enrolling={enrolment.isPending}
            enrolError={enrolment.isError ? enrolment.error : null}
            onEnrol={() => enrolment.mutate()}
            buying={checkout.isPending}
            buyError={checkout.isError ? checkout.error : null}
            onBuy={() => checkout.mutate()}
          />
        </aside>
      </div>
    </main>
  );
}

/* ----------------------------------------------------------------- breadcrumb ----- */

/**
 * Where this page sits. Two levels, not the prototype's three.
 *
 * The prototype's middle crumb is a topic ("Strategy"), and Open edX has no topic
 * taxonomy — the nearest thing is the org, which on this platform names the ACADEMY the
 * reader is already inside. A crumb that navigates you to where you already are is
 * furniture, so it is left out.
 */
function Breadcrumb({ title }: { title: string }) {
  return (
    <nav aria-label="Breadcrumb">
      <ol className="breadcrumb">
        <li className="breadcrumb-item">
          <Link to="/discover">{text('nav_discover', 'Courses')}</Link>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          {title}
        </li>
      </ol>
    </nav>
  );
}

/* ---------------------------------------------------------------------- hero ----- */

function CourseHero({
  course,
  about,
  inPaths,
}: {
  course: CourseDetail;
  about: CourseAbout | undefined;
  inPaths: LearningPathView[];
}) {
  // The BANNER, never the card — the card has this course's title typeset into it, and
  // behind an `<h1>` of the same words it renders as a grey watermark over the heading.
  // See `courseHeroImage`. Null here is ordinary and leaves the brand gradient.
  const image = courseHeroImage(course.media);
  const [artFailed, setArtFailed] = useState(false);

  const start = startLabel(course);
  // The author's marketing title wins over the display name when they differ; on most
  // courses they are the same string.
  const title = about?.title || course.name;
  const tagline = about?.subtitle || course.short_description || '';

  // Facts, in the order they answer "is this for me": how long, how much work, how big,
  // how it runs, when it opens. Only the ones that exist are built, so the row never has
  // a gap or a dangling separator.
  const facts: { icon: string; label: string }[] = [];
  if (about?.duration) facts.push({ icon: 'schedule', label: about.duration });
  if (course.effort) facts.push({ icon: 'timer', label: course.effort });
  if (about && about.outline.unitCount > 0) {
    facts.push({
      icon: 'view_list',
      // Lessons only, not "N sections · N lessons" — the separator inside a fact reads
      // as the separator BETWEEN facts and turns the row into one long ambiguous list.
      // The outline section states the full breakdown a few hundred pixels below.
      label: `${about.outline.unitCount} ${plural(about.outline.unitCount, 'lesson')}`,
    });
  }
  facts.push({
    icon: course.pacing === 'self' ? 'person' : 'groups',
    // 'self' and 'instructor' are the API's words; these are the learner's.
    label: course.pacing === 'self' ? 'Learn at your own pace' : 'Instructor paced',
  });
  if (start) facts.push({ icon: 'event', label: `Starts ${start}` });

  return (
    <header className="course__hero">
      {image && !artFailed && (
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className="course__hero-art"
          onError={() => setArtFailed(true)}
        />
      )}

      <div className="course__hero-body">
        <Link to="/discover" className="course__back">
          <Icon name="arrow_back" />
          All courses
        </Link>

        <PartOfPaths inPaths={inPaths} />

        <div>
          <span className="course__org">{course.org}</span>
        </div>

        {course.invitation_only && (
          <div>
            <span className="visibility-chip is-private" title="Invitation-only course">
              <Icon name="lock" />
              Private · invite only
            </span>
          </div>
        )}

        <h1>{title}</h1>
        {tagline && <p className="course__tagline">{tagline}</p>}

        <div className="course__hero-meta">
          {facts.map((fact, index) => (
            // Separators are SIBLINGS of the facts, not children of them — inside, the
            // flex gap puts the dot closer to its own fact than to the gap it divides,
            // and the row reads as if the first item had lost its label.
            <Fragment key={fact.label}>
              {index > 0 && <span className="dot-sep">·</span>}
              <span>
                <Icon name={fact.icon} />
                {fact.label}
              </span>
            </Fragment>
          ))}
        </div>
      </div>
    </header>
  );
}

/**
 * "Part of" — the learning paths this course belongs to, as chips in the hero.
 *
 * The prototype's `.part-of-chips`, in the same place and for the same reason: someone who
 * arrived on a single course should be able to see, without scrolling, that it is one step
 * of something larger. Renders nothing at all when the course is in no path, which is what
 * keeps this page identical to what it was for every course on an academy with no paths.
 */
function PartOfPaths({ inPaths }: { inPaths: LearningPathView[] }) {
  if (inPaths.length === 0) return null;

  return (
    <div className="course__part-of">
      <span className="course__part-of-label">Part of</span>
      {inPaths.map((view) => (
        <Link key={view.path.key} to={pathHref(view.path.slug)} className="course__part-of-chip">
          <Icon name="route" />
          {view.path.title}
        </Link>
      ))}
    </div>
  );
}

/**
 * The pathway panel on the enrolment card.
 *
 * Two different things to say, and which one depends on whether the learner is actually in
 * a path that contains this course:
 *
 *   in a path      where this course sits in it, and the path's progress. This is the
 *                  prototype's "Included with your learning path" panel, minus its claim
 *                  about not needing a separate purchase — there is no path-level purchase
 *                  here, so repeating that line would describe a product that does not exist
 *   not in one     that this course also counts towards a path they could join. An
 *                  invitation, not a status
 *
 * Nothing when the course belongs to no path.
 */
function PathContext({
  inPaths,
  pathPrices,
}: {
  inPaths: LearningPathView[];
  pathPrices: PricingSeam | undefined;
}) {
  if (inPaths.length === 0) return null;

  // The path the learner is actually in wins the panel. A course can sit in several; the
  // one they have joined is the one that changes what this page means to them.
  const joined = inPaths.find((view) => view.state !== 'not_enrolled');

  if (joined) {
    const position = joined.steps.findIndex((step) => step.linked && step.state !== 'completed');
    const step = joined.steps.find((candidate) => candidate.state !== 'completed');

    return (
      <div className="path-context">
        <p className="path-context__head">
          <Icon name="route" />
          Part of your learning path
        </p>
        <p>
          This course is{' '}
          {step ? (
            <>
              step {step.order} of {joined.stepCount} in{' '}
            </>
          ) : (
            'part of '
          )}
          <Link to={pathHref(joined.path.slug)}>{joined.path.title}</Link>
          {position === -1 && ', which you have finished'}.
        </p>
        <div className="path-context__progress">
          <div className="path-progress__row">
            <span>
              {joined.stepsCompleted} of {joined.stepCount} complete
            </span>
            <strong>{pct(joined.progress)}%</strong>
          </div>
          <div className="path-bar">
            <div
              className="path-bar__fill"
              data-done={joined.state === 'completed' || undefined}
              style={{ width: `${pct(joined.progress)}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  const [first] = inPaths;
  if (!first) return null;

  /**
   * THE BUNDLE PRICE OF A PATH THIS LEARNER HAS NOT JOINED (spec R6.4).
   *
   * The one cross-sell line on this page, and it is stated only when it is a real,
   * purchasable number: a path whose price is `unknown` gets NO sentence rather than a
   * hedged one, because "part of a path that may or may not cost something" is not
   * information. `pathwayPriceView` is what decides that — a path absent from a document
   * that loaded is free, and free paths say nothing here either, since "also available free
   * in a path" is a claim about enrolment this line is not the place for.
   *
   * `wasMinor`/`savingMinor` are deliberately not repeated here. The saving belongs to the
   * path's own page (R6.9), which is one click away and has room to state both numbers with
   * their relation; a bare "save RM 82" beside one course is the kind of half-comparison
   * NFR-A1 exists to stop.
   */
  const bundle = pathwayPriceView(pathPrices, first.path.key);
  const bundleLine =
    inPaths.length === 1 && bundle.mode === 'paid' && bundle.totalMinor !== null && bundle.currency
      ? `${formatMoney(bundle.totalMinor, bundle.currency)} for the whole path.`
      : null;

  return (
    <div className="path-context">
      <p className="path-context__head">
        <Icon name="route" />
        Counts towards a learning path
      </p>
      <p>
        {inPaths.length === 1 ? (
          <>
            This course is one of {first.stepCount} in{' '}
            <Link to={pathHref(first.path.slug)}>{first.path.title}</Link>.
          </>
        ) : (
          <>
            This course counts towards {inPaths.length} learning paths, including{' '}
            <Link to={pathHref(first.path.slug)}>{first.path.title}</Link>.
          </>
        )}
        {bundleLine && <> {bundleLine}</>}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------- about + copy ----- */

/**
 * "About this course".
 *
 * Three sources, tried in order, and the last one is the interesting case.
 *
 *   short_description   one paragraph, written for the catalogue card — almost always
 *                       the right thing here too
 *   overview            the author's full About HTML, but ONLY when a human wrote it;
 *                       `authoredOverview` returns null for the untouched template
 *   nothing             the section does not render
 *
 * The overview is rendered as HTML, as every Open edX front end renders it: it is
 * course-team content served from the tenant's own origin, which is the same trust level
 * as the courseware itself. The styling is scoped to `.course__overview` so an author's
 * stray tag cannot restyle the app around it.
 */
function AboutSection({ course, about }: { course: CourseDetail; about: CourseAbout | undefined }) {
  const summary = course.short_description?.trim() || about?.shortDescription?.trim() || '';
  const overview = authoredOverview(about?.overview ?? course.overview);

  // The subtitle is already the hero's tagline; repeating it as the body would print the
  // same sentence twice, one above the other.
  const body = summary && summary !== about?.subtitle?.trim() ? summary : '';

  if (!body && !overview) return null;

  return (
    <section className="course__section">
      <h2>About this course</h2>
      {body && <p className="course__prose">{body}</p>}
      {overview && <div className="course__overview" dangerouslySetInnerHTML={{ __html: overview }} />}
    </section>
  );
}

/** Courses the author says you should have done first. Linked, because you can go do them. */
function Prerequisites({ about }: { about: CourseAbout | undefined }) {
  const prerequisites = about?.prerequisites ?? [];
  if (prerequisites.length === 0) return null;

  return (
    <section className="course__section">
      <h2>Before you start</h2>
      <p className="course__section-note">
        The course team expects you to have completed{' '}
        {prerequisites.length === 1 ? 'this course' : 'these courses'} first.
      </p>
      <ul className="course__prereqs">
        {prerequisites.map((prerequisite) => (
          <li key={prerequisite.courseId}>
            <Link to={courseHref(prerequisite.courseId)}>
              <Icon name="school" />
              {prerequisite.title}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** `learning_info` — the one field on the admin's settings page that is literally this. */
function WhatYouWillLearn({ about }: { about: CourseAbout | undefined }) {
  const outcomes = about?.learningInfo ?? [];
  if (outcomes.length === 0) return null;

  return (
    <section className="course__section">
      <h2>What you'll learn</h2>
      <ul className="course__learn">
        {outcomes.map((outcome) => (
          <li key={outcome}>{outcome}</li>
        ))}
      </ul>
    </section>
  );
}

/* -------------------------------------------------------------------- grading ----- */

/**
 * "How you're graded", from the course's real grading policy.
 *
 * `grading` is null whenever the course has no graded content, so this section is simply
 * absent on a course that has no assessments. That is doing more work than it looks:
 * Studio seeds EVERY new course with the same four graders — Homework, Lab, Midterm,
 * Final — so the policy is never empty and rendering it unconditionally produces a
 * confident breakdown of a midterm nobody wrote. The server drops graders with no graded
 * subsection behind them and returns null when none are left; see `about.py`.
 *
 * `totalWeight` is printed rather than assumed. The platform does not enforce that the
 * weights add to 100, and a policy that does not is a course grading every learner in the
 * run against a scale that cannot reach full marks — worth flagging on the page where a
 * course team might actually see it.
 */
function HowYouAreGraded({ grading }: { grading: AboutGrading | null }) {
  if (!grading) return null;

  const balanced = Math.abs(grading.totalWeight - 100) < 0.01;

  return (
    <section className="course__section">
      <h2>How you're graded</h2>
      <p className="course__section-note">
        {grading.passMark === null
          ? 'Your final grade is weighted across these assessments.'
          : `Your final grade is weighted across these assessments. Pass mark is ${format(
              grading.passMark,
            )}%.`}
      </p>

      <div className="course__panel">
        <div className="grade-breakdown">
          {grading.graders.map((grader) => (
            <div className="grade-breakdown__row" key={grader.type}>
              <div>
                <span className="grade-breakdown__label">{grader.shortLabel || grader.type}</span>
                <span className="grade-breakdown__sub">
                  {grader.subsections} {plural(grader.subsections, 'assessment')}
                  {grader.dropCount > 0 && ` · lowest ${grader.dropCount} dropped`}
                </span>
              </div>
              {/* Width is the share of the TOTAL, not of 100 — on a policy that does not
                  add up, bars drawn against 100 would leave a phantom gap that looks
                  like a missing assessment rather than a broken policy. */}
              <div className="grade-breakdown__bar">
                <div
                  className="grade-breakdown__fill"
                  style={{
                    width: `${
                      grading.totalWeight > 0 ? (grader.weight / grading.totalWeight) * 100 : 0
                    }%`,
                  }}
                />
              </div>
              <div className="grade-breakdown__pct">{format(grader.weight)}%</div>
            </div>
          ))}
        </div>

        <div className="grade-breakdown__total" data-tone={balanced ? undefined : 'warning'}>
          <span>Total weighting</span>
          <span>
            {format(grading.totalWeight)}%
            {!balanced && ' — this policy does not add up to 100%'}
          </span>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------- outline ----- */

/** Which icon a unit gets. Kinds come from the server; `null` means it holds nothing typed. */
const UNIT_ICONS: Record<NonNullable<UnitKind>, string> = {
  video: 'play_circle',
  reading: 'description',
  quiz: 'quiz',
  assignment: 'assignment',
  discussion: 'forum',
};

/**
 * The syllabus, as sections you can open.
 *
 * A subsection scheduled ahead of today keeps its title and loses its unit list — the
 * server withholds the rows and returns only the count (see `about.py`). That state is
 * rendered explicitly rather than as an empty list, because "4 units, opening 1
 * September" and "no units" look identical otherwise and mean completely different
 * things to someone deciding whether to enrol.
 */
function CourseOutline({ outline }: { outline: AboutOutline | undefined }) {
  // First section open, the rest closed — the prototype's `is-open` on module one. It
  // shows the shape of the course without making the reader scroll past all of it.
  const [openSections, setOpenSections] = useState<Record<number, boolean>>({ 0: true });

  if (!outline || outline.sections.length === 0) return null;

  const scheduled = outline.sections.some(
    (section) => section.opens !== null || section.subsections.some((sub) => sub.opens !== null),
  );

  return (
    <section className="course__section">
      <h2>Course outline</h2>
      <p className="course__section-note">
        {outline.sectionCount} {plural(outline.sectionCount, 'section')} ·{' '}
        {outline.subsectionCount} {plural(outline.subsectionCount, 'part')} ·{' '}
        {outline.unitCount} {plural(outline.unitCount, 'lesson')}
      </p>

      {scheduled && (
        <div className="curriculum-legend">
          <span>
            <Icon name="lock_clock" />
            Some parts of this course open on a schedule — their lessons appear once they
            do.
          </span>
        </div>
      )}

      {outline.sections.map((section, index) => {
        const open = openSections[index] ?? false;
        const units = section.subsections.reduce((total, sub) => total + sub.unitCount, 0);

        return (
          <div
            className={`curriculum-module${open ? ' is-open' : ''}`}
            key={`${section.title}-${index}`}
          >
            <button
              type="button"
              className="curriculum-module__head"
              aria-expanded={open}
              onClick={() => setOpenSections((current) => ({ ...current, [index]: !open }))}
            >
              <h3>{section.title}</h3>
              <span className="curriculum-module__meta">
                <span>
                  {section.subsections.length} {plural(section.subsections.length, 'part')}
                  {units > 0 && ` · ${units} ${plural(units, 'lesson')}`}
                </span>
                {section.opens && (
                  <span className="schedule-pill">Opens {formatDate(section.opens)}</span>
                )}
                <Icon name="expand_more" className="chev" />
              </span>
            </button>

            {open && (
              <ul className="curriculum-module__list">
                {section.subsections.map((subsection, subIndex) => (
                  <Subsection key={`${subsection.title}-${subIndex}`} subsection={subsection} />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </section>
  );
}

function Subsection({ subsection }: { subsection: AboutSubsection }) {
  return (
    <>
      <li className="curriculum-unit">
        <div className="type-icon t-reading">
          <Icon name={subsection.graded ? 'grade' : 'folder_open'} />
        </div>
        <span className="curriculum-unit__title">
          <strong>{subsection.title}</strong>
        </span>
        {subsection.graded && subsection.assignmentType && (
          <span className="curriculum-unit__dur">Graded · {subsection.assignmentType}</span>
        )}
        {subsection.opens && (
          <span className="schedule-pill">Opens {formatDate(subsection.opens)}</span>
        )}
      </li>

      {subsection.units.map((unit, index) => (
        <li className="curriculum-unit curriculum-unit--child" key={`${unit.title}-${index}`}>
          <div className={`type-icon t-${unit.kind ?? 'reading'}`}>
            <Icon name={unit.kind ? UNIT_ICONS[unit.kind] : 'article'} />
          </div>
          <span className="curriculum-unit__title">{unit.title}</span>
        </li>
      ))}

      {/* Scheduled, so the rows are withheld — but the count is not, and saying how much
          is behind the date is the whole value of showing the row at all. */}
      {subsection.opens !== null && subsection.unitCount > 0 && (
        <li className="curriculum-unit curriculum-unit--child curriculum-unit--sealed">
          <div className="type-icon t-reading">
            <Icon name="lock" />
          </div>
          <span className="curriculum-unit__title">
            {subsection.unitCount} {plural(subsection.unitCount, 'lesson')}, available when
            this part opens
          </span>
        </li>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- instructors ----- */

function Instructors({ instructors }: { instructors: AboutInstructor[] }) {
  if (instructors.length === 0) return null;

  return (
    <section className="course__section">
      <h2>Meet your instructors</h2>
      <div className="instructors-grid">
        {instructors.map((instructor) => (
          <InstructorCard key={instructor.name} instructor={instructor} />
        ))}
      </div>
    </section>
  );
}

function InstructorCard({ instructor }: { instructor: AboutInstructor }) {
  const [expanded, setExpanded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  // Bios run long — authors paste a whole LinkedIn summary — so the toggle only appears
  // when there is genuinely something clamped behind it.
  const clampable = instructor.bio.length > 240;

  return (
    <article className={`instructor-card${expanded ? ' is-expanded' : ''}`}>
      <div className="instructor-card__photo">
        {instructor.image && !imageFailed ? (
          <img src={instructor.image} alt="" onError={() => setImageFailed(true)} />
        ) : (
          <span className="instructor-card__initials">{initials(instructor.name)}</span>
        )}
      </div>

      <div className="instructor-card__body">
        <h3>{instructor.name}</h3>
        {instructor.title && <p className="instructor-card__role">{instructor.title}</p>}
        {instructor.organization && (
          <p className="instructor-card__org">{instructor.organization}</p>
        )}
        {instructor.bio && <p className="instructor-card__bio">{instructor.bio}</p>}
        {clampable && (
          <button
            type="button"
            className="instructor-card__bio-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? 'Show less' : 'Read more'}
            <Icon name={expanded ? 'expand_less' : 'expand_more'} />
          </button>
        )}
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------- price ------ */

/**
 * The price on the course page (spec R6.4), in the `course__price` element this card has
 * always used.
 *
 * A WRAPPER AND NOTHING ELSE. Every decision inside it belongs to `pricing.ts` and
 * `shared/ui/price.tsx`: `PriceBlock` renders "Price unavailable" with its reason for
 * `unknown`, and the amount plus R5.4's processing disclosure for a paid course.
 *
 * FREE KEEPS ITS OWN LINE, AND IT KEEPS TODAY'S WORDS. `PriceBlock` renders nothing for a
 * free item — correct on a card, where the price slot is one line — but this page has
 * printed "Free to join" above the button since before there were prices, R6.4 lists it as
 * unchanged copy, and rollout Phase 1 requires the browser diff to be invisible on an
 * academy that prices nothing. Dropping it would have made every course page on every
 * existing tenant lose a line, which is a visible regression dressed up as tidiness.
 *
 * `ProcessingNote` is NOT repeated here: `PriceBlock` already ends with it. The second copy
 * on this page is the one beside the Buy button, which is a different place for a different
 * reason — the disclosure has to be next to the control that spends the money, and the two
 * are far enough apart on a tall card to need saying twice.
 *
 * This exists as a named component rather than an inline element so that the price on this
 * page is one thing that can be moved, and because the class name is the hook the stylesheet
 * and the spec both refer to.
 */
function CoursePrice({ view }: { view: PriceView }) {
  if (view.mode === 'free') {
    return <p className="course__price">Free to join</p>;
  }

  return (
    <div className="course__price">
      <PriceBlock view={view} />
    </div>
  );
}

/* -------------------------------------------------------------- action card ----- */

interface ActionCardProps {
  course: CourseDetail;
  about: CourseAbout | undefined;
  /** Empty on a course in no learning path, which leaves this card exactly as it was. */
  inPaths: LearningPathView[];
  /** Prices for those paths, for the one cross-sell line in the spec. May be undefined. */
  pathPrices: PricingSeam | undefined;
  /** Already resolved by the page, so this card never decides what a price means. */
  price: PriceView;
  metadata: CourseMetadata | undefined;
  metadataFailed: boolean;
  enrolling: boolean;
  enrolError: unknown;
  onEnrol: () => void;
  buying: boolean;
  buyError: unknown;
  onBuy: () => void;
}

function ActionCard({
  course,
  about,
  inPaths,
  pathPrices,
  price,
  metadata,
  metadataFailed,
  enrolling,
  enrolError,
  onEnrol,
  buying,
  buyError,
  onBuy,
}: ActionCardProps) {
  const image = courseImage(course.media);
  const [imageFailed, setImageFailed] = useState(false);

  const signedIn = Boolean(metadata?.username);
  const enrolled = metadata?.is_enrolled === true;
  // ONLY an explicit `false` blocks. `undefined` (loading) and `null` (couldn't tell)
  // must leave the button alone, or a slow or failed lookup silently disables enrolment
  // for verified learners — a far bigger failure than the one being prevented.
  const mustVerify = useAccountVerified(metadata?.username) === false;
  // Staff reach the courseware through their role, so "Enrol first" would be wrong.
  const staffAccess = metadata?.is_staff === true;

  /*
   * WHAT WAS HERE, AND WHY IT WAS A REVENUE BUG WAITING FOR A PRICE.
   *
   *     const free = (metadata?.course_modes ?? course.media ? metadata?.course_modes : undefined)
   *       ?.every((mode) => mode.slug === 'audit' || mode.slug === 'honor');
   *
   * `??` binds tighter than `?:`, so the ternary's condition was
   * `(metadata?.course_modes ?? course.media)` — the branch taken was decided by whether the
   * course had MEDIA, and `free` was `undefined` whenever the metadata had not arrived. The
   * line below it read `{free !== false && <p>Free to join</p>}`, so an undefined `free`
   * printed "Free to join". A course whose metadata request was slow, throttled or failed
   * advertised itself as free. Harmless while everything IS free; a shop giving stock away
   * during any backend hiccup the moment a price exists (spec R6.0, and the spec names this
   * exact expression).
   *
   * It is replaced wholesale, not repaired. `course_modes` is no longer consulted for
   * pricing at all: Open edX's modes are per-course-run rows with an upgrade-deadline
   * lifecycle and cannot express a pathway bundle, so prices are ours (spec Non-goals).
   * `price` comes from the pricing seam, where "we could not tell" is a state with a name.
   */
  // Annotated rather than inferred so the six states this card must handle are named where
  // the branching starts: `enrol`, `buy`, `sign_in_to_buy`, `enter`, `pending`, `unavailable`
  // (spec R6.10). `paymentPending` is deliberately not passed — it is sibling F's fact, and
  // inferring it from a missing enrolment would tell somebody their payment is processing
  // when it has actually failed.
  const buy: BuyState = buyState({ view: price, signedIn, owned: enrolled || staffAccess });

  return (
    <div className="course__card">
      {image && !imageFailed ? (
        <img src={image} alt="" className="course__card-image" onError={() => setImageFailed(true)} />
      ) : (
        <div className="course__card-image course__card-image--letter" aria-hidden="true">
          {course.name.charAt(0).toUpperCase()}
        </div>
      )}

      <div className="course__card-body">
        {/* Above the price and the button on purpose: it is the fact that changes what
            those two mean. */}
        <PathContext inPaths={inPaths} pathPrices={pathPrices} />

        {/*
          THE PRICE, and no price at all for somebody who already holds the course.
          A learner does not need to be told what they paid, and the enrolled branch below
          says what they can do instead (spec R6.4, OWNED).
        */}
        {buy !== 'enter' && <CoursePrice view={price} />}

        {enrolError !== null && (
          <Banner tone="danger">{explainEnrolmentFailure(enrolError, course)}</Banner>
        )}

        {/* The server's own sentence. Nothing has been charged — checkout had not started. */}
        {buyError !== null && (
          <Banner tone="danger">
            <strong>Checkout did not open.</strong> {explainPricingFailure(buyError)}
          </Banner>
        )}

        {metadataFailed ? (
          // Without the metadata we cannot tell enrolled from not, and a button that
          // guesses either way is worse than one that says it does not know.
          <Banner tone="warning">
            <strong>We could not check your enrolment.</strong> Reload the page to try
            again.
          </Banner>
        ) : !metadata ? (
          <Button variant="primary" busy>
            Checking…
          </Button>
        ) : buy === 'enter' ? (
          <>
            <Link to={learnHref(course.id)} className="ui-btn course__cta" data-variant="primary">
              <Icon name="play_arrow" />
              {enrolled ? 'Go to course' : 'Open course'}
            </Link>
            <p className="course__card-note">
              {enrolled ? (
                <>
                  <Icon name="check_circle" />
                  You are enrolled
                </>
              ) : (
                <>
                  <Icon name="shield_person" />
                  You have staff access to this course
                </>
              )}
            </p>
            <AccessNote metadata={metadata} />
            {/* Only for an ENROLLED learner — staff reach the courseware without an
                enrolment and the awards endpoint answers for the signed-in learner's own
                standing, which staff-without-enrolment do not have one of. Label follows
                what this page already knows about the course's grading policy: `null`
                means no graded content, which is the same fact the awards endpoint calls
                "ungraded" — see `shared/lib/catalogue.ts::AboutGrading`. */}
            {enrolled && (
              <Link
                to={gradesHref(course.id)}
                className="ui-btn course__cta"
                data-variant="secondary"
              >
                <Icon name="grade" />
                {about?.grading ? 'My grades' : 'My progress'}
              </Link>
            )}
          </>
        ) : buy === 'unavailable' ? (
          /*
            NO BUTTON, A STATED REASON, AND NOTHING CHARGED (spec R6.10, R6.4 NO-STRIPE).
            Modelled on the `metadataFailed` branch above, which already refuses to guess:
            "a button that guesses either way is worse than one that says it does not know."
            This covers all of it — a pricing outage, a malformed row, a price Stripe does
            not hold yet, a course taken off sale, and a tenant whose Stripe is not verified.
            The learner-visible truth is the same in every one: this cannot be bought now.
          */
          <UnavailableNotice reason={price.reason} fallback={PRICE_COPY.cannotBuyCourse} />
        ) : buy === 'pending' ? (
          <>
            <Button variant="primary" disabled busy>
              Payment pending
            </Button>
            <p className="course__card-note">
              <Icon name="schedule" />
              {PRICE_COPY.pending}
            </p>
          </>
        ) : buy === 'buy' ? (
          <>
            {/* `ui-btn` repeated for the same reason as the Enrol button below. */}
            <Button
              variant="primary"
              className="ui-btn course__cta"
              busy={buying}
              onClick={onBuy}
              // The verification gate COMPOSES WITH, and outranks, the buy state — exactly
              // as it does for a free enrolment. `mustVerify` is only ever an explicit
              // `false` from the lookup, so a slow verification read cannot block a sale.
              disabled={mustVerify}
            >
              {`Buy for ${priceLabel(price)}`}
            </Button>
            {mustVerify ? (
              <VerifyAccountNotice
                username={metadata?.username ?? ''}
                variant="inline"
                message="Verify your email address before buying. We sent you a link when you signed up."
              />
            ) : (
              /* The disclosure, again, next to the control that spends the money. No
                 mention of a code: promising a code field on somebody else's page is a
                 promise we cannot keep (D4, R5.9). */
              <ProcessingNote view={price} />
            )}
          </>
        ) : buy === 'sign_in_to_buy' ? (
          <>
            {/* `?buy=1`, the paid twin of `?enrol=1`, so the round trip keeps the intent
                and the purchase resumes itself rather than making them press Buy twice. */}
            <Link
              to={`/login?next=${encodeURIComponent(`/courses/${course.id}?buy=1`)}`}
              className="ui-btn course__cta"
              data-variant="primary"
            >
              Sign in to buy
            </Link>
            <p className="course__card-note">
              <Icon name="person_add" />
              <span>
                No account yet? <Link to="/register">Create one</Link> — it takes a minute.
              </span>
            </p>
          </>
        ) : signedIn ? (
          <>
            {/* `ui-btn` is repeated on purpose. `Button` spreads its props AFTER setting
                its own className, so a className passed in REPLACES the component's
                rather than adding to it — the Enrol button was rendering unstyled. */}
            <Button
              variant="primary"
              className="ui-btn course__cta"
              busy={enrolling}
              onClick={onEnrol}
              // Disabled, not hidden. The server refuses this enrolment anyway
              // (`account_not_activated`), and a button that vanishes leaves someone
              // hunting for it; one that is visibly unavailable, with the reason
              // directly beneath, explains itself.
              disabled={mustVerify}
            >
              {text('enroll_free', 'Enrol for free')}
            </Button>
            {mustVerify ? (
              <VerifyAccountNotice
                username={metadata?.username ?? ''}
                variant="inline"
                message="Verify your email address before enrolling. We sent you a link when you signed up."
              />
            ) : (
              <p className="course__card-note">
                <Icon name="lock_open" />
                You can leave at any time
              </p>
            )}
          </>
        ) : (
          <>
            {/* Back to THIS page after signing in, with the intent preserved, so the
                enrolment completes itself rather than making them press Enrol twice. */}
            <Link
              to={`/login?next=${encodeURIComponent(`/courses/${course.id}?enrol=1`)}`}
              className="ui-btn course__cta"
              data-variant="primary"
            >
              Sign in to enrol
            </Link>
            <p className="course__card-note">
              <Icon name="person_add" />
              <span>
                No account yet? <Link to="/register">Create one</Link> — it takes a minute.
              </span>
            </p>
          </>
        )}

        {/* The prototype's "this course includes" list. Every row is a fact read off the
            course, so a course with no outline and no schedule simply has a shorter
            list — none of them are the prototype's fixture copy. */}
        <ul className="course__card-facts">
          <li>
            <Icon name="language" />
            {course.pacing === 'self' ? 'Start whenever you like' : 'Runs on a schedule'}
          </li>
          {about && about.outline.unitCount > 0 && (
            <li>
              <Icon name="menu_book" />
              {about.outline.unitCount} {plural(about.outline.unitCount, 'lesson')} across{' '}
              {about.outline.sectionCount} {plural(about.outline.sectionCount, 'section')}
            </li>
          )}
          {about?.duration && (
            <li>
              <Icon name="schedule" />
              {about.duration}
            </li>
          )}
          {about?.grading && (
            <li>
              <Icon name="grade" />
              {about.grading.graders.length} graded{' '}
              {plural(about.grading.graders.length, 'assessment type')}
            </li>
          )}
          {course.invitation_only && (
            <li>
              <Icon name="mail" />
              Invitation only — the course team adds learners
            </li>
          )}
          {course.enrollment_end && (
            <li>
              <Icon name="event_busy" />
              Enrolment closes {formatDate(course.enrollment_end)}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

/**
 * Why the courseware will not open yet, in the platform's own words.
 *
 * `course_access.user_message` is already written for a learner ("Course has not
 * started"), so it is passed through rather than paraphrased — paraphrasing it means
 * maintaining a second copy of every access rule the platform has.
 */
function AccessNote({ metadata }: { metadata: CourseMetadata }) {
  const { has_access: hasAccess, user_message: message, error_code: code } = metadata.course_access;
  if (hasAccess || !message) return null;

  return (
    <Banner tone="info">
      {message}
      {code === 'course_not_started' && metadata.start && (
        <> It opens on {formatDate(metadata.start)}.</>
      )}
    </Banner>
  );
}

/* ------------------------------------------------------------- unavailable ----- */

/**
 * Everything that can stop this page rendering, said plainly.
 *
 * `NotOurCourse` is the one worth calling out: the detail endpoints answer for ANY
 * course from ANY tenant's hostname, so a course id from another academy loads
 * successfully at the API and is refused here instead. See the note in catalogue.ts.
 */
function CourseUnavailable({ error }: { error: unknown }) {
  const notFound = error instanceof ApiError && (error.status === 404 || error.status === 400);
  const otherTenant = error instanceof NotOurCourse;

  return (
    <main className="page page--message">
      <h1>{otherTenant || notFound ? 'We could not find that course' : 'That course did not load'}</h1>
      <p>
        {otherTenant
          ? 'It is not part of this academy. If someone sent you this link, they may have meant a different site.'
          : notFound
            ? 'The link may be wrong, or the course may have been unpublished since it was shared with you.'
            : 'The academy did not answer. Nothing is wrong with your account — please reload the page.'}
      </p>
      <p>
        <Link to="/discover" className="ui-btn" data-variant="primary">
          Browse the catalogue
        </Link>
      </p>
    </main>
  );
}

/* ------------------------------------------------------------------ helpers ----- */

/** "1 section" / "2 sections". Only ever used on words whose plural is a trailing 's'. */
function plural(count: number, word: string): string {
  return count === 1 ? word : `${word}s`;
}

/** Percentages arrive as floats; 40 should print as "40", not "40.00". */
function format(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}

/** Up to two initials, for an instructor with no photograph. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

// Re-exported type is unused at runtime; kept off the module surface deliberately.
export type { CatalogueCourse };
